/**
 * FEATURE 2: Best Performing - ISOLATED LOADER
 * Completely independent data loader for Feature 2 only
 * No conflicts with other features - has its own state and data sources
 */

import { useEffect, useRef } from "react";
import Papa from "papaparse";
import { fetchR2CsvText } from "../../utils/fetchR2Csv";

// ISOLATED CACHE - Only for Feature 2
const feature2Cache = new Map();

// RegionID mapping cache for all area types
const regionIdMapping = new Map();

/**
 * Isolated Data Loader for Feature 2: Best Performing
 * Handles performance rankings independently
 */
export function useFeature2DataLoader({
  areaType,
  setFeature2Data,
  setFeature2Loading,
  setFeature2Error,
  shouldLoad = true
}) {
  const hasLoadedRef = useRef(false);
  const hookId = useRef(Math.random().toString(36).substr(2, 9));

  console.log(`[FEATURE2_LOADER] Hook called with areaType: ${areaType}`);

  // Reset when area type changes
  useEffect(() => {
    if (!shouldLoad) return; // Guard clause inside useEffect
    
    hasLoadedRef.current = false;
    setFeature2Data({
      allDates: [],
      latestByMetro: {},
      scores: null,
      ranks: null,
      latestDate: "",
      chartData: []
    });
    setFeature2Loading(false);
    setFeature2Error(null);
    console.log(`[FEATURE2_LOADER] Reset for areaType: ${areaType}`);
  }, [areaType, shouldLoad, setFeature2Data, setFeature2Loading, setFeature2Error]);

  // Load data when area type is available
  useEffect(() => {
    if (!shouldLoad) return; // Guard clause inside useEffect
    if (!areaType || hasLoadedRef.current) {
      console.log(`[FEATURE2_LOADER] Skipping load - areaType: ${areaType}, hasLoaded: ${hasLoadedRef.current}`);
      return;
    }

    console.log(`[FEATURE2_LOADER] Starting data load for ${areaType}`);
    hasLoadedRef.current = true;
    setFeature2Loading(true);
    setFeature2Error(null);

    const loadFeature2Data = async () => {
      try {
        console.log(`[FEATURE2_LOADER] Starting loadFeature2Data for ${areaType}`);
        const levelDir = areaType.toLowerCase();
        
        // FEATURE 2 DATA SOURCE (single explicit source per level, no fallbacks)
        // Use the canonical Best_Performing_Rank files for ALL levels.
        const r2Key = `public/data/Best Performing/Best_Performing_Rank/best_performing_rank_${levelDir}.csv`;
        console.log(`[FEATURE2_LOADER] Attempt rankings from canonical rank file: ${r2Key}`);
        const rankingsText = await fetchR2CsvText(r2Key);
        
        // Parse rankings data (contains both rankings and snapshot data)
        Papa.parse(rankingsText, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: 'greedy',
          complete: (rankingsResult) => {
            try {
              console.log(`[FEATURE2_LOADER] Parsed ${rankingsResult.data.length} rows with complete data`);

              // Process rankings data (contains all needed data)
              const processedData = processRankingsDataDirectly(rankingsResult.data, areaType);

              // Reliability: mask metrics that aren't actually available for this month
              (async () => {
                try {
                  const { maskLatestByAvailability } = await import("../../utils/reliability");
                  const masked = await maskLatestByAvailability(areaType, processedData.latestByMetro);
                  setFeature2Data({ ...processedData, latestByMetro: masked });
                  setFeature2Loading(false);
                } catch (err) {
                  console.warn(`[FEATURE2_LOADER] Reliability masking failed, using unmasked data:`, err);
                  setFeature2Data(processedData);
                  setFeature2Loading(false);
                }
              })();

              console.log(`[FEATURE2_LOADER] Feature 2 data loaded successfully with rankings and snapshots`);
            } catch (error) {
              console.error(`[FEATURE2_LOADER] Error processing data:`, error);
              setFeature2Error(`Data processing error: ${error.message}`);
              setFeature2Loading(false);
            }
          },
          error: (error) => {
            console.error(`[FEATURE2_LOADER] Rankings parsing error:`, error);
            setFeature2Error(`Rankings parsing error: ${error.message}`);
            setFeature2Loading(false);
          }
        });

      } catch (error) {
        console.error(`[FEATURE2_LOADER] Load error:`, error);
        setFeature2Error(`Load error: ${error.message}`);
        setFeature2Loading(false);
      }
    };

    // Expose a manual trigger for debugging (call window.__bpDebugLoad() from console)
    if (typeof window !== 'undefined') {
      window.__bpDebugLoad = () => {
        console.error('[FEATURE2_LOADER] ⏩ Manual trigger: loadFeature2Data()');
        return loadFeature2Data();
      };
      // Allow query param ?forceBpLoad=1 to auto-trigger
      try {
        const u = new URL(window.location.href);
        if (u.searchParams.get('forceBpLoad') === '1') {
          console.error('[FEATURE2_LOADER] ⏩ forceBpLoad=1 detected, invoking loadFeature2Data()');
          loadFeature2Data();
        }
      } catch {}
    }

    loadFeature2Data().catch(error => {
      console.error(`[FEATURE2_LOADER] Async error in loadFeature2Data:`, error);
      setFeature2Error(`Data loading error: ${error.message}`);
      setFeature2Loading(false);
    });

  }, [areaType, shouldLoad, setFeature2Data, setFeature2Loading, setFeature2Error]);

  // Load RegionID mapping for chart data
  const loadRegionIdMapping = async (areaTypeParam = null) => {
    const targetAreaType = areaTypeParam || areaType;
    const cacheKey = `${targetAreaType}_mapping`;
    if (regionIdMapping.has(cacheKey)) {
      console.log(`[FEATURE2_LOADER] RegionID mapping already loaded for ${targetAreaType}: ${regionIdMapping.get(cacheKey).size} entries`);
      return;
    }

    try {
      const levelDir = targetAreaType.toLowerCase();
      console.log(`[FEATURE2_LOADER] Loading RegionID mapping for ${targetAreaType} from R2 dropdown data`);
      const r2Key = `public/data/Merged_No_Data/Standard/${levelDir}_5y_50k_standard_merged.csv`;
      const text = await fetchR2CsvText(r2Key);
      Papa.parse(text, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: 'greedy',
        complete: (result) => {
          console.log(`[FEATURE2_LOADER] RegionID mapping CSV parsed for ${targetAreaType}, rows: ${result.data.length}`);
          const mapping = new Map();
          result.data.forEach(row => {
            let nameKey;
            if (targetAreaType === 'metro') {
              nameKey = 'Metro';
            } else if (targetAreaType === 'state') {
              nameKey = 'State';
            } else if (targetAreaType === 'county') {
              nameKey = 'CountyName';
            } else if (targetAreaType === 'city') {
              nameKey = 'City';
            } else if (targetAreaType === 'zipcode') {
              nameKey = 'Zipcode';
            } else if (targetAreaType === 'neighborhood') {
              nameKey = 'Neighborhood';
            }
            
            if (row[nameKey] && row.RegionID) {
              mapping.set(row[nameKey], row.RegionID);
            }
          });
          regionIdMapping.set(cacheKey, mapping);
          console.log(`[FEATURE2_LOADER] RegionID mapping loaded for ${targetAreaType}: ${mapping.size} entries`);
        }
      });
    } catch (error) {
      console.error(`[FEATURE2_LOADER] Error loading RegionID mapping for ${targetAreaType}:`, error);
    }
  };

  // Load chart data on demand
  const loadChartData = async (selectedName, metric = 'Price', regionId = null) => {
    console.log(`[FEATURE2_LOADER] loadChartData called with selectedName: ${selectedName}, metric: ${metric}, regionId: ${regionId}`);
    
    if (!areaType || !selectedName) {
      console.log(`[FEATURE2_LOADER] Missing requirements - areaType: ${areaType}, selectedName: ${selectedName}`);
      return;
    }

    try {
      const levelDir = areaType.toLowerCase();
      
      // Build R2 key for chart data (use Pre-Standard by default)
      const preStandardKey = `public/data/Merged_Full_Data/${levelDir}_5y_50k_merged.csv`;
      const standardKey = `public/data/Merged_Full_Data/${levelDir}_5y_50k_merged.csv`;
      let r2KeyForChart = preStandardKey;
      if (!['metro','state','county','city','zipcode','neighborhood'].includes(levelDir)) {
        r2KeyForChart = standardKey;
      }
      console.log(`[FEATURE2_LOADER] Loading chart data from R2: ${r2KeyForChart}`);
      const text = await fetchR2CsvText(r2KeyForChart);
      console.log(`[FEATURE2_LOADER] CSV data loaded, length: ${text.length} characters`);
      
      Papa.parse(text, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: 'greedy',
        complete: (result) => {
          console.log(`[FEATURE2_LOADER] CSV parsed, rows: ${result.data.length}`);
          const chartData = processFeature2ChartData(result.data, selectedName, areaType, metric, regionId);
          console.log(`[FEATURE2_LOADER] Chart data processed:`, chartData.length, 'points');
          setFeature2Data(prev => ({ ...prev, chartData }));
          console.log(`[FEATURE2_LOADER] Chart data loaded for ${selectedName}, metric: ${metric}:`, chartData.length, 'points');
        }
      });

    } catch (error) {
      console.error(`[FEATURE2_LOADER] Chart data error:`, error);
    }
  };

  // Return Feature 2 specific utilities
  return {
    reloadFeature2: () => {
      hasLoadedRef.current = false;
      setFeature2Loading(true);
    },
    clearFeature2Cache: () => {
      feature2Cache.clear();
    },
    loadChartData,
    getFeature2CacheStats: () => ({
      cacheSize: feature2Cache.size,
      hookId: hookId.current
    })
  };
}

/**
 * Load latest data for Feature 2 snapshots
 */
async function loadLatestDataForFeature2(areaType, scores, ranks, setFeature2Data, setFeature2Loading, setFeature2Error) {
  try {
    const levelDir = areaType.toLowerCase();
    const latestKey = `public/data/Merged_Last_Month_Data/${levelDir}_5y_50k_merged.csv`;
    console.log(`[FEATURE2_LOADER] Loading latest data from R2: ${latestKey}`);
    const text = await fetchR2CsvText(latestKey);
    console.log(`[FEATURE2_LOADER] Received text length: ${text.length} characters`);
    
    Papa.parse(text, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: 'greedy',
      complete: (result) => {
        try {
          const rows = result.data || [];
          console.log(`[FEATURE2_LOADER] Parsed ${rows.length} latest rows`);
          
          // Debug: Log available fields in the first row
          if (rows.length > 0) {
            console.log(`[FEATURE2_LOADER] Available fields in Last Month CSV:`, Object.keys(rows[0]));
            console.log(`[FEATURE2_LOADER] Sample row:`, rows[0]);
          }

          // Process latest data
          const processedData = processLatestData(rows, areaType, scores, ranks);
          
          // Update Feature 2 state
          setFeature2Data(processedData);
          setFeature2Loading(false);
          
          console.log(`[FEATURE2_LOADER] Feature 2 data loaded successfully`);
        } catch (error) {
          console.error(`[FEATURE2_LOADER] Error processing latest data:`, error);
          setFeature2Error(`Latest data processing error: ${error.message}`);
          setFeature2Loading(false);
        }
      }
    });

  } catch (error) {
    console.error(`[FEATURE2_LOADER] Latest data load error:`, error);
    setFeature2Error(`Latest data load error: ${error.message}`);
    setFeature2Loading(false);
  }
}

/**
 * Process rankings data for Feature 2
 */
function processRankingsData(rows, areaType) {
  const nameKey = getNameKeyByAreaType(areaType);
  const scores = { performance: {}, potential: {} };
  const ranks = { performance: {}, potential: {} };

  rows.forEach((row, index) => {
    const name = row[nameKey] || row.AreaName || row.Name;
    if (!name) return;

    // Performance scores and ranks
    if (row.Score !== undefined && row.Rank !== undefined) {
      scores.performance[name] = row.Score;
      ranks.performance[name] = row.Rank;
    }
  });

  return { scores, ranks };
}


/**
 * Process rankings data directly for Feature 2 (contains all needed data)
 */
function processRankingsDataDirectly(rows, areaType) {
  const nameKey = getNameKeyByAreaType(areaType);
  const scores = { performance: {}, potential: {} };
  const ranks = { performance: {}, potential: {} };
  const latestByMetro = {};
  const allDates = new Set();

  rows.forEach((row, index) => {
    const name = row[nameKey] || row.AreaName || row.Name;
    if (!name) return;

    // Add date to allDates
    if (row.Date) allDates.add(row.Date);

    // Performance scores and ranks
    if (row.Score !== undefined && row.Rank !== undefined) {
      scores.performance[name] = row.Score;
      ranks.performance[name] = row.Rank;
    }

    // Store all row data for this area with correct field mapping for snapshots
    latestByMetro[name] = {
      ...row, // Keep all original fields
      // Map raw CSV fields to normalized field names expected by MetroDataTable
      // Price fields
      PriceDelta: row.Price_delta,
      PriceYoY: row.Price_YoY_delta,  // This IS the YoY value, not a delta of delta
      // Rent fields  
      RentDelta: row.Rent_delta,
      // Inventory fields
      InventoryDelta: row.Inventory_delta,
      // Days fields
      DaysDelta: row.Days_delta ?? row.DOM_delta,
      // Forecast fields (already have correct names: f1m, f3m, f12m)
      // No need to map these - they're already correct
    };
  });

  // Get latest date
  const latestDate = Array.from(allDates).sort().pop() || "";

  return {
    allDates: Array.from(allDates).sort(),
    latestByMetro,
    scores,
    ranks,
    latestDate,
    chartData: []
  };
}

/**
 * Process latest data for Feature 2
 */
function processLatestData(rows, areaType, scores, ranks) {
  const nameKey = getNameKeyByAreaType(areaType);
  
  // Convert to wide format if needed
  const formatted = convertToWideFormat(rows, nameKey);
  
  // Extract latest data
  const latestByMetro = {};
  const allDates = new Set();
  
  formatted.forEach(row => {
    if (row.Date) allDates.add(row.Date);
    if (row[nameKey]) {
      // Map forecast fields to expected aliases
      const processedRow = {
        ...row,
        // Forecast fields mapping
        Fcst1m: row.f1m || row.Fcst1m || row['1m'] || '--',
        Fcst3m: row.f3m || row.Fcst3m || row['3m'] || '--',
        Fcst12m: row.f12m || row.Fcst12m || row['12m'] || '--',
        // Other field mappings
        PriceYoY: row.PriceYoY || row['Price YoY'] || row.price_yoy || '--',
        RentYoY: row.RentYoY || row['Rent YoY'] || row.rent_yoy || '--',
        InventoryDelta: row.InventoryDelta || row['Inventory Delta'] || row.inventory_delta || '--'
      };
      latestByMetro[row[nameKey]] = processedRow;
    }
  });

  // Get latest date
  const latestDate = Array.from(allDates).sort().pop() || "";

  return {
    allDates: Array.from(allDates).sort(),
    latestByMetro,
    scores,
    ranks,
    latestDate,
    chartData: []
  };
}

/**
 * Helper function to pad zipcode to 5 digits
 */
function padZip5(zip) {
  return String(zip ?? "").padStart(5, "0");
}

/**
 * Process chart data for Feature 2 with RegionID matching
 */
function processFeature2ChartData(rows, selectedName, areaType, metric = 'Price', regionId = null) {
  console.log(`[FEATURE2_PROCESS_CHART_DATA] Processing chart data:`, {
    totalRows: rows.length,
    selectedName,
    areaType,
    metric
  });
  
  const nameKey = getNameKeyByAreaType(areaType);
  console.log(`[FEATURE2_PROCESS_CHART_DATA] Using nameKey: ${nameKey}`);
  
  // Map metric names to CSV column names
  const metricMap = {
    'Price': 'Price',
    'Rent': 'Rent',
    'Inventory': 'Inventory',
    'DOM': 'Days'
  };
  
  const fieldName = metricMap[metric] || 'Price';
  console.log(`[FEATURE2_PROCESS_CHART_DATA] Using fieldName: ${fieldName}`);
  
  let filteredRows = [];

  // Enforce 100% RegionID matching
  const effectiveRegionId = regionId;
  console.log(`[FEATURE2_PROCESS_CHART_DATA] Enforcing RegionID-only matching. RegionID: ${effectiveRegionId}`);
  if (effectiveRegionId == null) {
    console.error(`[FEATURE2_PROCESS_CHART_DATA] Missing RegionID for selection "${selectedName}" at ${areaType}. Returning empty series.`);
    return [];
  }
  filteredRows = rows.filter(row => row.RegionID === effectiveRegionId);
  console.log(`[FEATURE2_PROCESS_CHART_DATA] RegionID matches found:`, filteredRows.length);
  
  if (filteredRows.length > 0) {
    console.log(`[FEATURE2_PROCESS_CHART_DATA] Sample filtered row:`, {
      name: filteredRows[0][nameKey],
      regionId: filteredRows[0].RegionID,
      date: filteredRows[0].Date,
      value: filteredRows[0][fieldName]
    });
  }
  
  const result = filteredRows
    .map(row => ({
      date: row.Date,
      // Always map the selected metric to 'price' so the chart renders without needing to switch dataKey
      price: row[fieldName]
    }))
    .filter(row => row.price !== null && row.price !== undefined && !isNaN(row.price))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  
  console.log(`[FEATURE2_PROCESS_CHART_DATA] Final result:`, result.length, 'points');
  console.log(`[FEATURE2_PROCESS_CHART_DATA] Sample result:`, result.slice(0, 3));
  
  return result;
}

/**
 * Process chart data for Feature 2 (legacy function for backward compatibility)
 */
function processChartData(rows, selectedName, areaType) {
  const nameKey = getNameKeyByAreaType(areaType);
  
  return rows
    .filter(row => row[nameKey] === selectedName)
    .map(row => ({
      date: row.Date,
      price: row.Price,
      rent: row.Rent,
      inventory: row.Inventory,
      days: row.Days
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}

/**
 * Get name key by area type
 */
function getNameKeyByAreaType(areaType) {
  const nameKeyMap = {
    State: "State",
    Metro: "Metro", 
    // County data in all merged / linked dashboard files uses "CountyName",
    // not "County" – align with Feature 1 & Feature 4 loaders so lookups
    // and snapshot tables use the correct key.
    County: "CountyName",
    City: "City",
    Zipcode: "Zipcode",
    Neighborhood: "Neighborhood"
  };
  return nameKeyMap[areaType] || "Metro";
}

/**
 * Convert long format to wide format
 */
function convertToWideFormat(rows, nameKey) {
  // Simple conversion - assumes data is already in wide format
  // For more complex conversion, use the utility functions
  return rows;
}
